import React, { createContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import api from "@utils/api";
import fa from '@translations/fa.json';
const translations = { fa };
// Create the Context
export const ChartContext = createContext();

export const ChartProvider = ({ children }) => {

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [translatedTexts, setTranslatedTexts] = useState({});


  const [language, setLanguage] = useState("fa");
  const t = (key) => translations[language][key] || key;


  const fetchTexts = async (lang) => {
    const response = await api.get('/static/chart').then(res => res.data);
    setTranslatedTexts(response);
  };



  useEffect(() => {
    Cookies.set('language', language, { expires: 7000 })
    fetchTexts(language);
  }, []);



  useEffect(() => {
    const token = Cookies.get("token");
    setIsAuthenticated(!!token);
    setAuthLoading(false);
  }, []);

  const login = (token) => {
    Cookies.set("token", token, {
      secure: true,
      sameSite: "strict",
      expires: 7,
      path: "/",
    });
    setIsAuthenticated(true);
  };

  const logout = useCallback(() => {
    Cookies.remove("token", { secure: true, sameSite: "strict", path: "/" });
    setIsAuthenticated(false);
    window.location.href = "https://tablokhani.com/";
  });


  /*** Chart States and Functions ***/
  const [currentCandle, setCurrentCandle] = useState({});
  const [currentSymbol, setCurrentSymbol] = useState("فملی");
  const [symbols, setSymbols] = useState([]);
  const [symbolsID, setSymbolsID] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [selectedChart, setSelectedChart] = useState(Cookies.get("selectedChart") ?? 0);
  const [tvWidgetsComp, setTvWidgetsComp] = useState([]);
  const [widgets, setWidgets] = useState(["tv1"]);
  const [mainWidget, setMainWidget] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState("1D");
  const [replayMode, setReplayMode] = useState(false);

  const isMobileClient = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone|Opera Mini|IEMobile|Mobile/i.test(
    navigator.userAgent
  );

  const [isMobile, setIsMobile] = useState(isMobileClient);
  const [watcherOpen, setWatcherOpen] = useState(!isMobile);
  const [phoneSidebarOpen, setPhoneSidebarOpen] = useState(!isMobile);
  const [loadedServerData, setLoadedServerData] = useState({});
  const [activeWatcherItems, setActiveWatcherItems] = useState([]);
  const [tvWidgetsCompReady, setTvWidgetsCompReady] = useState(false);
  const [watchersTitle, setWatchersTitle] = useState(["پیشفرض"]);
  const [activeWatcherIndex, setActiveWatcherIndex] = useState(Cookies.get("activeWatcherIndex") ?? "bourse-50");
  const [allMarketWatcher, setAllMarketWatcher] = useState([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [allTvWidgetsCompReady, setAllTvWidgetsCompReady] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [isScriptReady, setIsScriptReady] = useState(false);
  const adjustedTypes = useRef([10]);
  const [watchersUpdated, setWatchersUpdated] = useState(false);
  const [permittedToLoad, setPermittedToLoad] = useState(true);
  const [autoSaveConf, setAutoSaveConf] = useState(true);



  useEffect(() => {
    // 1️⃣ Get token from URL
    const params = new URLSearchParams(window.location.search);
    const s_ymbol = params.get("symbol");



    if (s_ymbol) {
      setCurrentSymbol(s_ymbol)
      setWatcherOpen(false)

      // permittedToLoad=false still stands: it stops TVChart's loadAllcharts()
      // from overwriting `symbols` with whatever generic layout the user last
      // saved, which would fight with the specific symbol this link opened.
      setPermittedToLoad(false)
      setSymbols([s_ymbol])

      // `layouts` still needs populating (separately from loadAllcharts()'s
      // symbol-setting side effects above) so TradingView's own
      // loadChartFromServer({id: `single@${symbol}`}) - fired automatically
      // once the chart mounts - can find and restore this symbol's
      // previously-saved drawings instead of always starting blank.
      //
      // setIsScriptReady(true) is deliberately held until this resolves,
      // not fired immediately alongside it. isScriptReady is what gates the
      // widget-creation effect in TVChart - firing it right away (this
      // fetch is still an in-flight network request at that point) let the
      // chart widget mount and immediately call loadChartFromServer() while
      // `layouts` was still [], so getChartContent() found nothing (silent
      // catch, no error surfaced) and the chart came up blank. Confirmed
      // live: drew a line, refreshed, the line didn't come back. Waiting
      // for this fetch first removes that race entirely.
      api.get("/chart/1.3/charts").then((response) => {
        if (response?.data?.data?.length) {
          setLayouts(response.data.data);
        }
      }).catch(() => { }).finally(() => {
        setIsScriptReady(true)
      });

      // symbol is deliberately NOT removed from the URL (token still is, in
      // AuthContext) - this used to call params.delete("symbol") +
      // history.replaceState, which left the browser on a bare "/" after
      // the first load. Refreshing that bare URL skipped this whole branch
      // (s_ymbol was null), falling through to loadAllcharts()'s hardcoded
      // default symbol instead of the ticker the user actually opened.
      // Keeping it in the URL makes a refresh re-run this same branch and
      // reliably reopen the same ticker every time.
      //
      // autoSaveConf is also left at its default (true) now - it was being
      // force-disabled here, meaning any drawing made from this symbol-deep-
      // link entry point (the normal way users reach this chart) never got
      // saved at all, regardless of refreshing.
    }
  }, []);


  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      login,
      logout,
      authLoading,
      loadedServerData,
      adjustedTypes,
      setLoadedServerData,
      activeWatcherItems,
      permittedToLoad, setPermittedToLoad,
      tvWidgetsCompReady,
      isSearchModalOpen,
      setIsSearchModalOpen,
      watchersUpdated,
      setWatchersUpdated,
      setTvWidgetsCompReady,
      setActiveWatcherItems,
      currentCandle,
      setCurrentCandle,
      currentSymbol,
      setCurrentSymbol,
      watcherOpen,
      autoSaveConf, setAutoSaveConf,
      setWatcherOpen,
      symbols,
      setSymbols,
      symbolsID,
      setSymbolsID,
      layouts,
      setLayouts,
      selectedChart,
      setSelectedChart,
      tvWidgetsComp,
      setTvWidgetsComp,
      widgets,
      setWidgets,
      mainWidget,
      setMainWidget,
      selectedInterval,
      setSelectedInterval,
      replayMode,
      setReplayMode,
      setWatchersTitle,
      watchersTitle,
      activeWatcherIndex,
      setActiveWatcherIndex,
      allMarketWatcher,
      setAllMarketWatcher,
      allTvWidgetsCompReady,
      setAllTvWidgetsCompReady,
      isScriptReady,
      setIsScriptReady,
      templateLoaded,
      setTemplateLoaded,
      setPhoneSidebarOpen,
      phoneSidebarOpen,
      translatedTexts,
      setTranslatedTexts,
      t,
    }),
    [
      isAuthenticated,
      login,
      logout,
      authLoading,
      currentCandle,
      setCurrentCandle,
      currentSymbol,
      setCurrentSymbol,
      symbols,
      setSymbols,
      symbolsID,
      setSymbolsID,
      layouts,
      setLayouts,
      selectedChart,
      setSelectedChart,
      tvWidgetsComp,
      setTvWidgetsComp,
      widgets,
      setWidgets,
      mainWidget,
      setMainWidget,
      selectedInterval,
      setSelectedInterval,
      replayMode,
      setReplayMode,
      translatedTexts,
      setTranslatedTexts,
      t,
    ]
  );

  return <ChartContext.Provider value={contextValue}>{children}</ChartContext.Provider>;
};
